import React, { useState } from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  Alert,
  Tooltip
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon,
  Article as ArticleIcon,
  Language as WebIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  X as XIcon,
  Forum as ThreadsIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { deleteArticle, bulkDeleteArticles, bulkRescrapeArticles } from '../../../../services/api';
import { scraperConfigAPI } from '../../../../services/scraperConfigAPI';
import ConfirmDialog from '../../../molecules/ConfirmDialog/ConfirmDialog';
import ModernToast from '../../../molecules/ModernToast/ModernToast';
import CheckboxAtom from '../../../atoms/Checkbox/Checkbox';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import { operationalFeedback } from '../../../../design-system/tokens';

// Função para formatar números com separadores de milhares
const formatNumber = (number) => {
  if (!number || number === 0) return '0';
  return number.toLocaleString('pt-BR');
};

// Logo customizado do Threads (logo oficial da Meta)
const CustomThreadsIcon = (props) => (
  <svg viewBox="0 0 192 192" width="24" height="24" fill="currentColor" {...props}>
    <path d="M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z" />
  </svg>
);

const ExistingArticlesModal = ({
  open,
  onClose,
  existingArticles = [],
  onViewArticle,
  onShareArticle,
  onArticlesUpdated, // Callback para quando a lista mudar (opcional)
  onRescrapeStarted // Novo callback para notificar início de bulk rescrape
}) => {
  const [localArticles, setLocalArticles] = useState([]);
  const [loadingAction, setLoadingAction] = useState(null); // 'delete' | 'rescrape' | null
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState(null);

  // Estados para seleção em massa
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmBulkDeleteOpen, setConfirmBulkDeleteOpen] = useState(false);
  const [confirmSingleDeleteOpen, setConfirmSingleDeleteOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);

  // Estado para notificações (Toast)
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const infoAlertSx = {
    mb: 2.5,
    bgcolor: 'background.paper',
    color: 'text.primary',
    border: '1px solid',
    borderColor: 'info.main',
    borderLeft: `${operationalFeedback.alert.accentBorderWidth}px solid`,
    borderLeftColor: 'info.main',
    borderRadius: `${operationalFeedback.alert.radius}px`,
    boxShadow: 'none',
    '& .MuiAlert-icon': {
      color: 'info.main'
    }
  };

  // Sincronizar localArticles quando o prop mudar
  React.useEffect(() => {
    setLocalArticles(existingArticles);
  }, [existingArticles]);

  const getMediaIcon = (mediaType) => {
    switch (mediaType) {
      case 'YouTube':
        return <YouTubeIcon color="error" />;
      case 'Instagram':
        return <InstagramIcon color="secondary" />;
      case 'Twitter':
        return <XIcon color="primary" />;
      case 'Threads':
        return <CustomThreadsIcon />;
      case 'Online':
        return <WebIcon color="primary" />;
      default:
        return <ArticleIcon color="action" />;
    }
  };

  const getMediaColor = (mediaType) => {
    switch (mediaType) {
      case 'YouTube':
        return 'error';
      case 'Instagram':
        return 'secondary';
      case 'Twitter':
        return 'primary';
      case 'Threads':
        return 'primary';
      case 'Online':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Data não disponível';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const handleViewArticle = (article) => {
    if (onViewArticle) {
      onViewArticle(article);
    }
  };

  const handleShareArticle = (article) => {
    if (onShareArticle) {
      onShareArticle(article);
    }
  };

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleToggleSelect = (articleId) => {
    if (!articleId) return;
    setSelectedIds(prev =>
      prev.includes(articleId)
        ? prev.filter(id => id !== articleId)
        : [...prev, articleId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(localArticles.map(art => art._id || art.id || art.articleId).filter(Boolean));
    } else {
      setSelectedIds([]);
    }
  };

  const confirmDeleteSingle = (articleId) => {
    if (!articleId) return;
    setArticleToDelete(articleId);
    setConfirmSingleDeleteOpen(true);
  };

  const handleDeleteArticle = async () => {
    if (!articleToDelete) return;

    setLoadingAction('delete');
    setActionId(articleToDelete);
    setError(null);

    try {
      await deleteArticle(articleToDelete);

      // Remover da lista local
      const updatedList = localArticles.filter(art => (art._id || art.id) !== articleToDelete);
      setLocalArticles(updatedList);
      setSelectedIds(prev => prev.filter(id => id !== articleToDelete));

      if (onArticlesUpdated) {
        onArticlesUpdated(updatedList);
      }

      showToast('Artigo excluído com sucesso');
    } catch (err) {
      console.error('❌ Erro ao excluir artigo:', err);
      setError(`Erro ao excluir: ${err.message || 'Falha na comunicação com o servidor'}`);
    } finally {
      setLoadingAction(null);
      setActionId(null);
      setArticleToDelete(null);
      setConfirmSingleDeleteOpen(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    setLoadingAction('bulk-delete');
    setError(null);

    try {
      await bulkDeleteArticles(selectedIds);

      // Remover da lista local
      const updatedList = localArticles.filter(art => {
        const artId = art._id || art.id || art.articleId;
        return !selectedIds.includes(artId);
      });
      setLocalArticles(updatedList);
      setSelectedIds([]);

      if (onArticlesUpdated) {
        onArticlesUpdated(updatedList);
      }

      showToast(`${updatedList.length === 0 ? 'Todos os artigos' : 'Artigos selecionados'} excluídos com sucesso`);
    } catch (err) {
      console.error('❌ Erro ao excluir artigos em massa:', err);
      setError(`Erro ao excluir artigos: ${err.message || 'Falha na comunicação'}`);
    } finally {
      setLoadingAction(null);
      setConfirmBulkDeleteOpen(false);
    }
  };

  const handleRescrapeArticle = async (article) => {
    const articleId = article._id || article.id || article.articleId;
    const url = article.siteUrl || article.url;
    if (!url) {
      setError('URL não encontrada para este artigo.');
      return;
    }

    setLoadingAction('rescrape');
    setActionId(articleId);
    setError(null);

    try {
      const tags = article.tags?.map(t => t.name || t) || [];
      const response = await scraperConfigAPI.startSmartScraping([url], tags, { force: true });

      if (response.success) {
        showToast('Recadastramento iniciado com sucesso!');
      } else {
        throw new Error(response.message || 'Erro ao iniciar re-cadastramento');
      }
    } catch (err) {
      console.error('❌ Erro ao re-scrapear artigo:', err);
      setError(`Erro ao re-scrapear: ${err.message || 'Falha na comunicação'}`);
    } finally {
      setLoadingAction(null);
      setActionId(null);
    }
  };

  const handleBulkRescrape = async () => {
    if (selectedIds.length === 0) return;

    setLoadingAction('bulk-rescrape');
    setError(null);

    try {
      const response = await bulkRescrapeArticles(selectedIds);

      if (response.data?.success || response.success) {
        const jobId = response.data?.data?.jobId || response.data?.jobId;
        showToast('Recadastramento em massa iniciado!');
        setSelectedIds([]);

        // Notificar pai sobre o Job ID iniciado para acompanhamento
        if (onRescrapeStarted && jobId) {
          onRescrapeStarted(jobId);
        }
      } else {
        throw new Error(response.message || 'Erro ao iniciar recadastramento em massa');
      }
    } catch (err) {
      console.error('❌ Erro ao recadastrar em massa:', err);
      setError(`Erro ao recadastrar: ${err.message || 'Falha na comunicação'}`);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <PlatformDialog
      open={open}
      onClose={handleClose}
      maxWidth={localArticles.length <= 1 ? 'md' : 'lg'}
      fullWidth
    >
      <PlatformDialogTitle
        title="Artigos Já Existentes no Sistema"
        subtitle={`${existingArticles.length} artigo${existingArticles.length !== 1 ? 's' : ''} encontrado${existingArticles.length !== 1 ? 's' : ''} em sua base de dados`}
        icon={<ArticleIcon />}
        onClose={handleClose}
      />

      <DialogContent sx={{ pt: 2.5, pb: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {localArticles.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Nenhum artigo existente foi encontrado nesta base.
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={infoAlertSx}>
              <Typography variant="body2">
                <strong>Dica:</strong> Estes artigos já estão em sua base de dados.
                Você pode visualizá-los, compartilhá-los ou optar por <strong>Recadastrar</strong> (atualizar conteúdo) ou <strong>Excluir</strong>.
              </Typography>
            </Alert>

            {/* Toolbar de Ações em Massa */}
            <Box sx={{
              mb: 2,
              px: 1.5,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckboxAtom
                  checked={selectedIds.length === localArticles.length && localArticles.length > 0}
                  indeterminate={selectedIds.length > 0 && selectedIds.length < localArticles.length}
                  onChange={(checked) => handleSelectAll(checked)}
                />
                <Typography variant="body2" fontWeight={600}>
                  {selectedIds.length > 0
                    ? `${selectedIds.length} selecionado(s)`
                    : 'Selecionar todos os artigos'}
                </Typography>
              </Box>

              {selectedIds.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setConfirmBulkDeleteOpen(true)}
                    disabled={loadingAction !== null}
                  >
                    Excluir Selecionados
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleBulkRescrape}
                    disabled={loadingAction !== null}
                  >
                    Recadastrar Selecionados
                  </Button>
                </Box>
              )}
            </Box>

            <Grid container spacing={1.5}>
              {localArticles.map((article, index) => (
                <Grid item xs={12} md={localArticles.length === 1 ? 12 : 6} key={article._id || index}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      bgcolor: 'background.paper',
                      backgroundImage: 'none',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      boxShadow: 'none',
                      transition: 'background-color 0.18s ease, border-color 0.18s ease',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderColor: 'text.secondary'
                      },
                      position: 'relative',
                      opacity: (loadingAction && actionId === (article._id || article.id || article.articleId)) ? 0.6 : 1
                    }}
                  >
                    {/* Header com tipo de mídia e Checkbox */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      pb: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}>
                      <CheckboxAtom
                        checked={selectedIds.includes(article._id || article.id || article.articleId)}
                        onChange={() => handleToggleSelect(article._id || article.id || article.articleId)}
                        sx={{ mr: 1 }}
                      />
                      {article.mediaType && getMediaIcon(article.mediaType)}
                      <Typography variant="subtitle2" sx={{ ml: article.mediaType ? 1 : 0, flexGrow: 1, color: 'text.secondary' }}>
                        {article.mediaType || 'Artigo'}
                      </Typography>
                      {article.mediaType && (
                        <Chip
                          label={article.mediaType}
                          size="small"
                          color={getMediaColor(article.mediaType)}
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {/* Conteúdo do artigo */}
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Typography
                        variant="h6"
                        component="h3"
                        gutterBottom
                        sx={{
                          fontSize: '1rem',
                          fontWeight: 600,
                          lineHeight: 1.3,
                          color: 'text.primary'
                        }}
                      >
                        {truncateText(article.title, 80)}
                      </Typography>

                      {article.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {truncateText(article.description, 120)}
                        </Typography>
                      )}

                      {/* Informações adicionais */}
                      <Box sx={{ mb: 2 }}>
                        {article.author && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Autor: {article.author}
                          </Typography>
                        )}
                        {article.siteName && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Fonte: {article.siteName}
                          </Typography>
                        )}
                        {article.date && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Data: {formatDate(article.date)}
                          </Typography>
                        )}
                      </Box>

                      {/* Métricas por plataforma */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        {/* YouTube */}
                        {article.mediaType === 'YouTube' && (
                          <>
                            <Chip
                              label={`Views ${formatNumber(article.viewsNumber || 0)}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`Público ${formatNumber(article.subscribersNumber || 0)}`}
                              size="small"
                              variant="outlined"
                            />
                          </>
                        )}

                        {/* Twitter */}
                        {article.mediaType === 'Twitter' && (
                          <>
                            <Chip
                              label={`Views ${formatNumber(article.viewsNumber || 0)}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`Público ${formatNumber(article.subscribersNumber || 0)}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`Likes ${formatNumber(article.likesNumber || 0)}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`Comentários ${formatNumber(article.commentsNumber || 0)}`}
                              size="small"
                              variant="outlined"
                            />
                          </>
                        )}

                        {/* Instagram */}
                        {article.mediaType === 'Instagram' && (
                          <>
                            <Chip
                              label={`Views ${formatNumber(article.viewsNumber || 0)}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`Likes ${formatNumber(article.likesNumber || 0)}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`Comentários ${formatNumber(article.commentsNumber || 0)}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`Público ${formatNumber(article.subscribersNumber || 0)}`}
                              size="small"
                              variant="outlined"
                            />
                          </>
                        )}

                        {/* Facebook */}
                        {article.mediaType === 'Facebook' && (
                          <>
                            <Chip
                              label={`Likes ${formatNumber(article.likesNumber || 0)}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`Comentários ${formatNumber(article.commentsNumber || 0)}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`Público ${formatNumber(article.subscribersNumber || 0)}`}
                              size="small"
                              variant="outlined"
                            />
                          </>
                        )}

                        {/* Valoração - Todas as plataformas */}
                        {article.valuation && article.valuation > 0 && (
                          <Chip
                            label={`R$ ${article.valuation.toLocaleString('pt-BR')}`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      {/* Tags */}
                      {article.tags && article.tags.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Tags:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {article.tags.slice(0, 3).map((tag, tagIndex) => (
                              <Chip
                                key={tagIndex}
                                label={tag.name || tag}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            ))}
                            {article.tags.length > 3 && (
                              <Chip
                                label={`+${article.tags.length - 3}`}
                                size="small"
                                variant="outlined"
                                color="default"
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                    </CardContent>

                    {/* Ações */}
                    <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {/* Ações de Gestão (Delete/Rescrape) */}
                        <Tooltip title="Excluir este artigo permanentemente">
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => confirmDeleteSingle(article._id || article.id || article.articleId)}
                            disabled={loadingAction === 'delete' && actionId === (article._id || article.id)}
                          >
                            Excluir
                          </Button>
                        </Tooltip>

                        <Tooltip title="Recadastrar este artigo (atualiza conteúdo e métricas)">
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<RefreshIcon />}
                            onClick={() => handleRescrapeArticle(article)}
                            disabled={loadingAction === 'rescrape' && actionId === (article._id || article.id)}
                          >
                            Recadastrar
                          </Button>
                        </Tooltip>

                        <Tooltip title="Visualizar artigo público">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewArticle(article)}
                          >
                            Ver
                          </Button>
                        </Tooltip>

                        <Tooltip title="Compartilhar link público">
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<ShareIcon />}
                            onClick={() => handleShareArticle(article)}
                            sx={{ borderRadius: 1 }}
                          >
                            Compartilhar
                          </Button>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose} variant="text">
          Fechar
        </Button>
        <Button
          variant="contained"
          onClick={handleClose}
          startIcon={<OpenInNewIcon />}
          sx={{ borderRadius: 1 }}
        >
          Continuar com URLs Novas
        </Button>
      </DialogActions>

      {/* Modais de Confirmação e Notificação */}
      <ConfirmDialog
        open={confirmSingleDeleteOpen}
        title="Confirmar exclusão"
        message="Tem certeza que deseja excluir este artigo? Esta ação não pode ser desfeita."
        onConfirm={handleDeleteArticle}
        onCancel={() => setConfirmSingleDeleteOpen(false)}
      />

      <ConfirmDialog
        open={confirmBulkDeleteOpen}
        title="Excluir múltiplos artigos"
        message={`Tem certeza que deseja excluir os ${selectedIds.length} artigos selecionados? Esta ação não pode ser desfeita.`}
        onConfirm={handleBulkDelete}
        onCancel={() => setConfirmBulkDeleteOpen(false)}
      />

      <ModernToast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
        position="bottom-center"
      />
    </PlatformDialog>
  );
};

export default ExistingArticlesModal;
